const SELECTORS = {
  prevButton: "[data-product-gallery-prev]",
  nextButton: "[data-product-gallery-next]",
  thumbnails: "[data-product-gallery-thumbnail]",
  thumbnailContainer: "[data-product-gallery-list]",
  stage: "[data-pg-stage]",
  stageImage: "[data-pg-stage-image]",
  playOverlay: "[data-pg-play-overlay]",
  videoToggle: "[data-pg-video-toggle]",
  toggleIconPause: "[data-pg-video-toggle-icon-pause]",
  toggleIconPlay: "[data-pg-video-toggle-icon-play]",
  videoTemplate: "[data-pg-video-template]",
  stageVideoTemplate: "[data-pg-stage-video-template]",
};

class ProductGallery extends HTMLElement {
  #indicator = null;

  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    try {
      this.#init();
    } catch (error) {
      console.error("[ProductGallery:init]", error);
    }
  }

  #init() {
    this.#bindElements();

    if (!this.allThumbs.length || !this.stage) return;

    this.#bindEvents();
    this.#syncInitialStageMode();

    if (this.allThumbs.length > 1) {
      this.#update();
    }
  }

  #bindElements() {
    this.prevButton = this.querySelector(SELECTORS.prevButton);
    this.nextButton = this.querySelector(SELECTORS.nextButton);
    this.allThumbs = Array.from(this.querySelectorAll(SELECTORS.thumbnails));
    this.thumbnailContainer = this.querySelector(SELECTORS.thumbnailContainer);
    this.stage = this.querySelector(SELECTORS.stage);
    this.stageImage = this.querySelector(SELECTORS.stageImage);
    this.playOverlay = this.querySelector(SELECTORS.playOverlay);
    this.videoToggle = this.querySelector(SELECTORS.videoToggle);
  }

  #bindEvents() {
    this.prevButton?.addEventListener("click", () => this.navigate(-1));
    this.nextButton?.addEventListener("click", () => this.navigate(1));

    this.thumbnailContainer?.addEventListener("click", (event) => {
      const clicked = event.target.closest(SELECTORS.thumbnails);
      if (!clicked) return;
      this.currentIndex = this.allThumbs.indexOf(clicked);
      this.#update();
    });

    this.playOverlay?.addEventListener("click", (event) => {
      event.preventDefault();
      try {
        this.#startStageVideo();
      } catch (error) {
        console.error("[ProductGallery:startStageVideo]", error);
      }
    });

    this.videoToggle?.addEventListener("click", (event) => {
      event.preventDefault();
      try {
        this.#toggleStageVideo();
      } catch (error) {
        console.error("[ProductGallery:toggleStageVideo]", error);
      }
    });
  }

  navigate(direction) {
    const newIndex = this.currentIndex + direction;
    if (newIndex >= 0 && newIndex < this.allThumbs.length) {
      this.currentIndex = newIndex;
      this.#update();
    }
  }

  #update() {
    this.#updateActiveThumb();
    this.#updateStage();
    this.#updateButtons();
    this.#indicator?.setIndex(this.currentIndex);
    this.#automaticScroll();
  }

  #updateActiveThumb() {
    this.allThumbs.forEach((thumb, index) => {
      thumb.classList.toggle("is-active", index === this.currentIndex);
    });
  }

  #updateStage() {
    const thumb = this.allThumbs[this.currentIndex];
    if (!thumb || !this.stage) return;

    this.#cleanupStageVideo();

    const mediaType = thumb.dataset.pgMediaType ?? "image";
    const newSrc = thumb.dataset.fullSrc ?? thumb.querySelector("img")?.src ?? "";
    const newAlt = thumb.getAttribute("aria-label");

    if (this.stageImage) {
      this.stageImage.classList.remove("hidden");
      if (mediaType === "image") {
        this.#crossFadeStageImage(newSrc, newAlt);
      } else {
        this.stageImage.classList.remove("is-fading");
        this.stageImage.src = newSrc;
        if (newAlt) this.stageImage.alt = newAlt;
      }
    }

    if (mediaType === "video" || mediaType === "external_video") {
      this.stage.dataset.pgFeaturedMediaType = mediaType;
      if (mediaType === "external_video") {
        this.stage.dataset.pgFeaturedEmbedUrl = thumb.dataset.pgEmbedUrl ?? "";
        this.stage.dataset.pgFeaturedHost = thumb.dataset.pgHost ?? "";
      } else {
        delete this.stage.dataset.pgFeaturedEmbedUrl;
        delete this.stage.dataset.pgFeaturedHost;
        this.#mountStageVideoTemplateFromThumb(thumb);
      }
      this.playOverlay?.removeAttribute("hidden");
      this.videoToggle?.setAttribute("hidden", "");
    } else {
      this.stage.dataset.pgFeaturedMediaType = "image";
      delete this.stage.dataset.pgFeaturedEmbedUrl;
      delete this.stage.dataset.pgFeaturedHost;
      this.playOverlay?.setAttribute("hidden", "");
      this.videoToggle?.setAttribute("hidden", "");
    }
  }

  async #crossFadeStageImage(newSrc, newAlt) {
    if (!this.stageImage || !newSrc) return;
    if (this.stageImage.src === newSrc) return;

    this.stageImage.classList.add("is-fading");
    this.stage?.classList.add("is-loading");

    const preload = new Image();
    preload.src = newSrc;
    try {
      if (typeof preload.decode === "function") await preload.decode();
    } catch (_) {
      // Decode kann bei nicht-CORS-fähigen Bildern fehlschlagen — trotzdem fortfahren
    }

    this.stageImage.src = newSrc;
    if (newAlt) this.stageImage.alt = newAlt;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.stageImage.classList.remove("is-fading");
        this.stage?.classList.remove("is-loading");
      });
    });
  }

  #syncInitialStageMode() {
    const featuredType = this.stage?.dataset.pgFeaturedMediaType;
    if (featuredType === "video" || featuredType === "external_video") {
      this.playOverlay?.removeAttribute("hidden");
    }
  }

  #mountStageVideoTemplateFromThumb(thumb) {
    const template = thumb.querySelector(SELECTORS.videoTemplate);
    if (!template) return;
    const existing = this.stage.querySelector(SELECTORS.stageVideoTemplate);
    if (existing) existing.remove();
    const clone = template.cloneNode(true);
    clone.removeAttribute("data-pg-video-template");
    clone.setAttribute("data-pg-stage-video-template", "");
    this.stage.appendChild(clone);
  }

  #cleanupStageVideo() {
    const existingVideo = this.stage?.querySelector("video");
    if (existingVideo) {
      existingVideo.pause();
      existingVideo.remove();
    }
    const existingIframe = this.stage?.querySelector("iframe");
    if (existingIframe) existingIframe.remove();
  }

  #startStageVideo() {
    const featuredType = this.stage?.dataset.pgFeaturedMediaType;
    if (featuredType === "video") {
      const template = this.stage.querySelector(SELECTORS.stageVideoTemplate);
      if (!template) return;
      const video = document.createElement("video");
      video.className = "absolute inset-0 w-full h-full object-cover z-0";
      video.setAttribute("preload", "metadata");
      video.setAttribute("playsinline", "");
      video.muted = true;
      video.loop = true;
      video.appendChild(template.content.cloneNode(true));
      video.addEventListener("play", () => this.#syncToggleIcon("playing"));
      video.addEventListener("pause", () => this.#syncToggleIcon("paused"));
      this.stage.appendChild(video);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => console.error("[ProductGallery:videoPlay]", error));
      }
      this.stageImage?.classList.add("hidden");
      this.playOverlay?.setAttribute("hidden", "");
      this.videoToggle?.removeAttribute("hidden");
    } else if (featuredType === "external_video") {
      const embedUrl = this.stage.dataset.pgFeaturedEmbedUrl;
      const host = this.stage.dataset.pgFeaturedHost;
      if (!embedUrl) return;
      const iframe = document.createElement("iframe");
      iframe.src = this.#buildExternalAutoplayUrl(embedUrl, host);
      iframe.className = "absolute inset-0 w-full h-full z-0";
      iframe.setAttribute("allow", "autoplay; fullscreen; encrypted-media; picture-in-picture");
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");
      this.stage.appendChild(iframe);
      this.stageImage?.classList.add("hidden");
      this.playOverlay?.setAttribute("hidden", "");
    }
  }

  #buildExternalAutoplayUrl(embedUrl, host) {
    const sep = embedUrl.includes("?") ? "&" : "?";
    if (host === "youtube") {
      return `${embedUrl}${sep}autoplay=1&mute=1&playsinline=1&controls=0&rel=0`;
    }
    if (host === "vimeo") {
      return `${embedUrl}${sep}autoplay=1&muted=1&loop=1&playsinline=1`;
    }
    return `${embedUrl}${sep}autoplay=1`;
  }

  #toggleStageVideo() {
    const video = this.stage?.querySelector("video");
    if (!video) return;
    if (video.paused) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {});
      }
    } else {
      video.pause();
    }
  }

  #syncToggleIcon(state) {
    const pauseIcon = this.querySelector(SELECTORS.toggleIconPause);
    const playIcon = this.querySelector(SELECTORS.toggleIconPlay);
    if (state === "playing") {
      pauseIcon?.classList.remove("hidden");
      playIcon?.classList.add("hidden");
    } else {
      pauseIcon?.classList.add("hidden");
      playIcon?.classList.remove("hidden");
    }
  }

  #updateButtons() {
    if (!this.prevButton || !this.nextButton) return;
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex === this.allThumbs.length - 1;
  }

  #automaticScroll() {
    const currentThumb = this.allThumbs[this.currentIndex];
    if (!currentThumb || !this.thumbnailContainer) return;

    const containerTop = this.thumbnailContainer.getBoundingClientRect().top;
    const thumbTop = currentThumb.getBoundingClientRect().top;

    this.thumbnailContainer.scrollTo({
      top: this.thumbnailContainer.scrollTop + thumbTop - containerTop,
      behavior: "smooth",
    });
  }
}

if (!customElements.get("product-gallery")) {
  customElements.define("product-gallery", ProductGallery);
}
